/**
 * "In attesa di risposta": thread in cui l'utente ha mandato l'ultimo messaggio e
 * non ha ancora ricevuto risposta. Un tap su "Sollecita" genera un promemoria AI
 * e apre il composer pronto da inviare.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Loader2, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { followUpsApi, aiApi, type FollowUp } from '@/lib/api/endpoints';
import { useComposer } from '@/stores/composer.store';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/client';

const SOLLECITO_INSTR =
  'Scrivi un sollecito cortese e breve in italiano: non ho ancora ricevuto risposta al mio messaggio precedente, chiedo gentilmente un aggiornamento. Tono professionale, mai insistente.';

export function FollowUpSection() {
  const q = useQuery({ queryKey: ['follow-ups'], queryFn: () => followUpsApi.list(3) });
  const openCompose = useComposer((s) => s.openCompose);
  const [busy, setBusy] = useState<string | null>(null);

  const items = q.data?.followUps ?? [];
  if (q.isLoading || items.length === 0) return null;

  async function sollecita(f: FollowUp) {
    setBusy(f.thread_id);
    try {
      const { body } = await aiApi.smartReply({ threadId: f.thread_id, tone: 'formale', instructions: SOLLECITO_INSTR });
      openCompose({
        inReplyToMessageId: f.message_id,
        threadId: f.thread_id,
        to: f.to ? [{ name: f.to_name ?? undefined, address: f.to }] : [],
        subject: f.subject.toLowerCase().startsWith('re:') ? f.subject : `Re: ${f.subject}`,
        body,
      });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Generazione sollecito fallita');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-amber-700 border-b border-amber-200">
        <Clock className="h-4 w-4" /> In attesa di risposta · {items.length}
      </div>
      <div className="divide-y divide-amber-100">
        {items.map((f) => (
          <div key={f.thread_id} className="flex items-center justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{f.subject}</div>
              <div className="text-xs text-muted-foreground truncate">
                a {f.to_name || f.to} · inviata {formatDistanceToNow(new Date(f.last_sent_at), { addSuffix: true, locale: it })}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => void sollecita(f)} disabled={busy === f.thread_id} className="shrink-0">
              {busy === f.thread_id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
              Sollecita
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
